import { useCallback, useState } from "react";
import { type DropEvent, useDropzone } from "react-dropzone";
import { useParams } from "react-router-dom";
import { Upload, X, AlertCircle, FileText } from "lucide-react";
import prettyBytes from "pretty-bytes";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";

interface FileUploadAreaProps {
  /** Callback when a file is selected (not uploaded yet) */
  onFileSelected: (file: File) => void;
  /** Currently selected file (if any) */
  selectedFile: File | null;
  /** Callback to clear the selected file */
  onClearFile?: () => void;
  disabled?: boolean;
  /** True while the file is being uploaded */
  isUploading?: boolean;
  /** Upload progress percentage (0-100) */
  uploadProgress?: number;
  /** Optional message to show when uploading will override existing ingestion */
  overrideMessage?: string;
  acceptedFileTypes?: Record<string, string[]>;
  maxSize?: number;
}

/**
 * FileUploadArea
 *
 * A drag-and-drop file selection component that stores the file locally.
 * The file will be uploaded when the user clicks "Continue".
 */
export const FileUploadArea = ({
  onFileSelected,
  selectedFile,
  onClearFile,
  disabled = false,
  isUploading = false,
  uploadProgress,
  overrideMessage,
  acceptedFileTypes = { "text/csv": [".csv"], "text/tab-separated-values": [".tsv"] },
  maxSize = 100 * 1024 * 1024, // 100 MB default
}: FileUploadAreaProps) => {
  const { datasetId } = useParams();
  const [dragActive, setDragActive] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleFileSelection = useCallback(
    (file: File, uploadMethod: "drag_drop" | "browse") => {
      setLocalError(null);

      // Validate file type
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      const acceptedExtensions = Object.values(acceptedFileTypes).flat();
      if (!acceptedExtensions.some((ext) => fileExtension === ext.toLowerCase())) {
        const errorMsg = `File type not supported. Accepted types: ${acceptedExtensions.join(
          ", "
        )}`;
        setLocalError(errorMsg);
        return;
      }

      // Validate file size
      if (file.size > maxSize) {
        const errorMsg = `File size exceeds maximum of ${prettyBytes(maxSize)}`;
        setLocalError(errorMsg);
        return;
      }

      // File is valid, track and store it
      trackEvent("file_upload_started", {
        dataset_id: datasetId,
        file_name: file.name,
        file_size_bytes: file.size,
        file_type: file.type,
        upload_method: uploadMethod,
      });
      trackEvent("file_selected", {
        dataset_id: datasetId,
        source: "local_upload",
        file_name: file.name,
        file_size_bytes: file.size,
      });
      onFileSelected(file);
    },
    [onFileSelected, acceptedFileTypes, maxSize, datasetId]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[], event: DropEvent) => {
      setDragActive(false);
      setLocalError(null);

      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors[0]?.code === "file-too-large") {
          setLocalError(`File size exceeds maximum of ${prettyBytes(maxSize)}`);
        } else if (rejection.errors[0]?.code === "file-invalid-type") {
          setLocalError(
            `File type not supported. Accepted types: ${Object.values(acceptedFileTypes)
              .flat()
              .join(", ")}`
          );
        } else {
          setLocalError(rejection.errors[0]?.message || "File rejected");
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        const uploadMethod = event instanceof DragEvent ? "drag_drop" : "browse";
        handleFileSelection(acceptedFiles[0], uploadMethod);
      }
    },
    [handleFileSelection, maxSize, acceptedFileTypes]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
    accept: acceptedFileTypes,
    maxSize,
    maxFiles: 1,
    disabled: disabled,
    noClick: true,
    noKeyboard: true,
  });

  const displayError = localError;
  const hasFile = !!selectedFile;

  return (
    <div className="space-y-3">
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center transition-colors min-h-[180px]",
          dragActive && "border-primary bg-primary/5",
          !dragActive && !hasFile && "border-gray-300 bg-gray-50",
          hasFile && "border-green-300 bg-green-50",
          disabled && "opacity-50 cursor-not-allowed",
          !disabled && "cursor-pointer hover:border-gray-400"
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center gap-3 h-full">
          {hasFile ? (
            <>
              <FileText className="h-8 w-8 text-green-500" />
              <div className="text-center">
                <p className="text-sm font-medium text-green-700">{selectedFile.name}</p>
                <p className="text-xs text-gray-500 mt-1">{prettyBytes(selectedFile.size)}</p>
              </div>
              {isUploading ? (
                <div className="w-full space-y-1">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress ?? 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-right">
                    {Math.round(uploadProgress ?? 0)}%
                  </p>
                </div>
              ) : (
                onClearFile && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearFile();
                    }}
                  >
                    Remove File
                  </Button>
                )
              )}
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-gray-400" />
              <div>
                <p className="text-gray-700 mb-1 font-medium">
                  {overrideMessage
                    ? "Drag and drop your file here or browse to start a new validation"
                    : "Drag and drop your file here"}
                </p>
                <p className="text-sm text-gray-500 mb-3">or</p>
                <Button type="button" variant="outline" onClick={() => open()} disabled={disabled}>
                  Browse Files
                </Button>
                {overrideMessage && <p className="text-xs text-gray-600 mt-3">{overrideMessage}</p>}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Error display */}
      {displayError && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700 font-medium">File Error</p>
            <p className="text-sm text-red-600">{displayError}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setLocalError(null);
            }}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
